import { useQuery } from '@tanstack/react-query';
import { Eye, Edit, Trash2, KeyRound } from 'lucide-react';
import { permissionModuleService } from '@/services/permission-module.service';

interface PermissionSelectorProps {
  selectedPermissions: Record<string, boolean>;
  onChange: (permissions: Record<string, boolean>) => void;
  rolePermissions: string[]; // Base permissions from selected role
}

interface ModulePermission {
  name: string;
  permissions: {
    view: string;
    edit?: string;
    delete?: string;
    /** Permissions outside the view/edit/delete convention
     *  (e.g. review_stage_1 / review_stage_2 for the editorial workflow) */
    other: string[];
  };
  category: string;
}

/** Human label for a non-standard permission key */
function permissionLabel(permission: string): string {
  const KNOWN_LABELS: Record<string, string> = {
    review_stage_1: 'Stage 1 — Linguist',
    review_stage_2: 'Stage 2 — Professor',
    review_assign: 'Assign reviewers',
  };
  return (
    KNOWN_LABELS[permission] ||
    permission.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function PermissionSelector({
  selectedPermissions,
  onChange,
  rolePermissions
}: PermissionSelectorProps) {
  // Fetch all modules from database
  const { data: dbModules = [] } = useQuery({
    queryKey: ['permission-modules-selector'],
    queryFn: async () => {
      const token = localStorage.getItem('accessToken') || '';
      return await permissionModuleService.getAllModules(token, { isActive: true });
    },
  });

  // Convert database modules to ModulePermission format
  const allModulePermissions: ModulePermission[] = dbModules.map(module => {
    const perms = module.permissions;

    // Find view, edit, and delete permissions from the permissions array
    const viewPerm = perms.find(p => p.includes('_view'));
    const editPerm = perms.find(p => p.includes('_edit'));
    const deletePerm = perms.find(p => p.includes('_delete'));

    // Anything else (e.g. editorial stage permissions) is rendered as a
    // generic toggle so it can be granted per individual user
    const otherPerms = perms.filter(p => p !== viewPerm && p !== editPerm && p !== deletePerm);

    return {
      name: module.name,
      permissions: {
        view: viewPerm || '',
        edit: editPerm,
        delete: deletePerm,
        other: otherPerms
      },
      category: module.category
    };
  });

  // Group by category
  const groupedModules = allModulePermissions.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {} as Record<string, ModulePermission[]>);

  const handlePermissionToggle = (permission: string) => {
    const newPermissions = { ...selectedPermissions };
    if (newPermissions[permission]) {
      newPermissions[permission] = false;
    } else {
      newPermissions[permission] = true;
    }
    onChange(newPermissions);
  };

  const isPermissionFromRole = (permission: string) => {
    return rolePermissions.includes(permission);
  };

  const isPermissionSelected = (permission: string) => {
    return selectedPermissions[permission] === true;
  };

  return (
    <div className="space-y-6">
      {Object.entries(groupedModules).map(([category, modules]) => (
        <div key={category} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide">
            {category}
          </h4>
          <div className="space-y-3">
            {modules.map((module) => (
                <div key={module.name} className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 text-sm mb-2">
                        {module.name}
                      </h5>
                      <div className="flex items-center space-x-2">
                      {/* View Permission */}
                      {module.permissions.view && (
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md border transition-all cursor-pointer ${
                          isPermissionSelected(module.permissions.view)
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-white border-gray-300 hover:border-blue-300'
                        }`}>
                          <input
                            type="checkbox"
                            checked={isPermissionSelected(module.permissions.view)}
                            onChange={() => handlePermissionToggle(module.permissions.view)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                          <Eye className="w-4 h-4 text-blue-600" />
                          <span className="text-xs font-medium text-gray-700">View</span>
                          {isPermissionFromRole(module.permissions.view) && (
                            <span className="text-xs text-blue-500 font-medium">(from role)</span>
                          )}
                        </label>
                      )}

                      {/* Edit Permission */}
                      {module.permissions.edit && (
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md border transition-all cursor-pointer ${
                          isPermissionSelected(module.permissions.edit)
                            ? 'bg-green-50 border-green-300'
                            : 'bg-white border-gray-300 hover:border-green-300'
                        }`}>
                          <input
                            type="checkbox"
                            checked={isPermissionSelected(module.permissions.edit)}
                            onChange={() => handlePermissionToggle(module.permissions.edit!)}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                          />
                          <Edit className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-medium text-gray-700">Edit</span>
                          {isPermissionFromRole(module.permissions.edit) && (
                            <span className="text-xs text-green-500 font-medium">(from role)</span>
                          )}
                        </label>
                      )}

                      {/* Delete Permission */}
                      {module.permissions.delete && (
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md border transition-all cursor-pointer ${
                          isPermissionSelected(module.permissions.delete)
                            ? 'bg-red-50 border-red-300'
                            : 'bg-white border-gray-300 hover:border-red-300'
                        }`}>
                          <input
                            type="checkbox"
                            checked={isPermissionSelected(module.permissions.delete)}
                            onChange={() => handlePermissionToggle(module.permissions.delete!)}
                            className="rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                          />
                          <Trash2 className="w-4 h-4 text-red-600" />
                          <span className="text-xs font-medium text-gray-700">Delete</span>
                          {isPermissionFromRole(module.permissions.delete) && (
                            <span className="text-xs text-red-500 font-medium">(from role)</span>
                          )}
                        </label>
                      )}

                      {/* Other permissions (e.g. editorial review stages) */}
                      {module.permissions.other.map((permission) => (
                        <label
                          key={permission}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-md border transition-all cursor-pointer ${
                            isPermissionSelected(permission)
                              ? 'bg-purple-50 border-purple-300'
                              : 'bg-white border-gray-300 hover:border-purple-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isPermissionSelected(permission)}
                            onChange={() => handlePermissionToggle(permission)}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                          />
                          <KeyRound className="w-4 h-4 text-purple-600" />
                          <span className="text-xs font-medium text-gray-700">{permissionLabel(permission)}</span>
                          {isPermissionFromRole(permission) && (
                            <span className="text-xs text-purple-500 font-medium">(from role)</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
